import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import PlanUpgrade from '../components/PlanUpgrade';
import {
  ChevronLeft, FolderOpen, Archive, Users, Settings, Zap,
  File, Folder, ChevronRight, Edit3, Trash2, RefreshCw,
  Plus, Download, RotateCcw, Lock, Unlock, Shield, ShieldOff,
  UserX, Save, AlertTriangle, Terminal, Loader, HardDrive,
  Server, Cpu, Play
} from 'lucide-react';

// ─── FILE MANAGER ────────────────────────────────────────────────
const FileManager = ({ serverId }) => {
  const [files, setFiles] = useState([]);
  const [dir, setDir] = useState('/');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { name, content }
  const [saving, setSaving] = useState(false);
  const [demo, setDemo] = useState(false);

  const loadFiles = useCallback(async (directory = '/') => {
    setLoading(true);
    try {
      const { data } = await api.get(`/servers/${serverId}/files`, { params: { directory } });
      setFiles(data.files);
      setDir(directory);
      setDemo(data.demo || false);
    } catch {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => { loadFiles('/'); }, [loadFiles]);

  const openFile = async (name) => {
    const path = dir === '/' ? `/${name}` : `${dir}/${name}`;
    try {
      const { data } = await api.get(`/servers/${serverId}/files/contents`, { params: { file: path } });
      setEditing({ name: path, content: data.content });
    } catch {
      toast.error('Could not open file');
    }
  };

  const saveFile = async () => {
    setSaving(true);
    try {
      await api.post(`/servers/${serverId}/files/write`, { file: editing.name, content: editing.content });
      toast.success('File saved!');
      setEditing(null);
    } catch {
      toast.error('Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const deleteFile = async (name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.post(`/servers/${serverId}/files/delete`, { files: [name] });
      toast.success('Deleted');
      loadFiles(dir);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const breadcrumbs = dir.split('/').filter(Boolean);

  if (editing) return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-mono text-sm text-gray-300">{editing.name}</span>
        </div>
        <button
          onClick={saveFile}
          disabled={saving}
          className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-gray-950 font-bold px-4 py-2 rounded-xl text-sm transition-colors"
        >
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
      <textarea
        value={editing.content}
        onChange={(e) => setEditing({ ...editing, content: e.target.value })}
        className="flex-1 bg-gray-950 border border-gray-700 rounded-xl p-4 font-mono text-sm text-gray-200 focus:outline-none focus:border-green-500 resize-none"
        style={{ minHeight: '500px' }}
        spellCheck={false}
      />
    </div>
  );

  return (
    <div>
      {demo && (
        <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-2.5 text-yellow-400 text-sm mb-4">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Demo mode — connect Pterodactyl to manage real files
        </div>
      )}

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1.5 mb-4 text-sm">
        <button onClick={() => loadFiles('/')} className="text-green-400 hover:text-green-300 font-mono">/</button>
        {breadcrumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
            <button
              onClick={() => loadFiles('/' + breadcrumbs.slice(0, i + 1).join('/'))}
              className="text-gray-400 hover:text-white font-mono transition-colors"
            >
              {crumb}
            </button>
          </React.Fragment>
        ))}
        <button onClick={() => loadFiles(dir)} className="ml-auto text-gray-500 hover:text-white transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* File list */}
      <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader className="w-6 h-6 text-green-400 animate-spin" />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Empty directory</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Size</th>
                <th className="text-left px-4 py-3 font-medium">Modified</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {files.map((file) => (
                <tr key={file.name} className="hover:bg-gray-800/30 transition-colors group">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => file.is_file ? openFile(file.name) : loadFiles(`${dir === '/' ? '' : dir}/${file.name}`)}
                      className="flex items-center gap-2.5 text-left"
                    >
                      {file.is_file
                        ? <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        : <Folder className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                      }
                      <span className={`${file.is_file ? 'text-gray-200 hover:text-white' : 'text-yellow-400 hover:text-yellow-300'} transition-colors`}>
                        {file.name}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{formatSize(file.size)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {file.modified_at ? new Date(file.modified_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                      {file.is_file && (
                        <button onClick={() => openFile(file.name)} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors" title="Edit">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => deleteFile(file.name)} className="p-1.5 hover:bg-red-500/10 rounded-lg text-gray-400 hover:text-red-400 transition-colors" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── BACKUPS ─────────────────────────────────────────────────────
const Backups = ({ serverId }) => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [backupName, setBackupName] = useState('');
  const [demo, setDemo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/servers/${serverId}/backups`);
      setBackups(data.backups);
      setDemo(data.demo || false);
    } catch {
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => { load(); }, [load]);

  const createBackup = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/servers/${serverId}/backups`, { name: backupName || `Backup ${new Date().toLocaleDateString()}` });
      setBackups((prev) => [data.backup, ...prev]);
      setBackupName('');
      toast.success('Backup started!');
    } catch {
      toast.error('Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (uuid, name) => {
    if (!window.confirm(`Restore "${name}"? Your current world will be replaced and the server will restart.`)) return;
    try {
      await api.post(`/servers/${serverId}/backups/${uuid}/restore`);
      toast.success('Restore started — server will restart shortly');
    } catch {
      toast.error('Failed to restore backup');
    }
  };

  const deleteBackup = async (uuid) => {
    if (!window.confirm('Delete this backup? This cannot be undone.')) return;
    try {
      await api.delete(`/servers/${serverId}/backups/${uuid}`);
      setBackups((prev) => prev.filter((b) => b.uuid !== uuid));
      toast.success('Backup deleted');
    } catch {
      toast.error('Failed to delete backup');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'In progress...';
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {demo && (
        <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-2.5 text-yellow-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Demo mode — connect Pterodactyl for real backups
        </div>
      )}

      {/* Create backup */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
        <h3 className="font-bold text-white mb-4">Create Backup</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            placeholder="Backup name (optional)"
            className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm transition-colors"
          />
          <button
            onClick={createBackup}
            disabled={creating}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-gray-950 font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
          >
            {creating ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </div>
        <p className="text-gray-500 text-xs mt-2">Backups are stored for 7 days. Max 2 backups on your plan.</p>
      </div>

      {/* Backup list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader className="w-6 h-6 text-green-400 animate-spin" /></div>
      ) : backups.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 border border-gray-800 border-dashed rounded-2xl">
          <Archive className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No backups yet</p>
          <p className="text-gray-600 text-sm mt-1">Create your first backup above</p>
        </div>
      ) : (
        <div className="space-y-3">
          {backups.map((backup) => (
            <div key={backup.uuid} className="bg-gray-900 border border-gray-800 rounded-2xl p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  {backup.is_locked ? <Lock className="w-5 h-5 text-blue-400" /> : <Archive className="w-5 h-5 text-blue-400" />}
                </div>
                <div>
                  <div className="font-semibold text-white flex items-center gap-2">
                    {backup.name}
                    {backup.is_locked && <span className="text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full">Locked</span>}
                  </div>
                  <div className="text-gray-500 text-sm mt-0.5">
                    {formatBytes(backup.bytes)} · {new Date(backup.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => restoreBackup(backup.uuid, backup.name)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-yellow-500/10 hover:border-yellow-500/20 hover:text-yellow-400 border border-gray-700 text-gray-300 text-sm rounded-xl transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </button>
                {!backup.is_locked && (
                  <button
                    onClick={() => deleteBackup(backup.uuid)}
                    className="p-2 bg-gray-800 hover:bg-red-500/10 hover:text-red-400 border border-gray-700 text-gray-400 rounded-xl transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── PLAYERS ─────────────────────────────────────────────────────
const Players = ({ serverId }) => {
  const [data, setData] = useState({ whitelist: [], ops: [], banned_players: [] });
  const [loading, setLoading] = useState(true);
  const [playerInput, setPlayerInput] = useState('');
  const [activeList, setActiveList] = useState('whitelist');
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    api.get(`/servers/${serverId}/players`).then(({ data: d }) => {
      setData(d);
      setDemo(d.demo || false);
    }).catch(() => toast.error('Failed to load players')).finally(() => setLoading(false));
  }, [serverId]);

  const sendCommand = async (command) => {
    try {
      await api.post(`/servers/${serverId}/players/command`, { command });
      toast.success(`Command sent: ${command}`);
      // Give Minecraft a moment to write the json files, then refresh
      setTimeout(() => {
        api.get(`/servers/${serverId}/players`).then(({ data: d }) => setData(d)).catch(() => {});
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send command');
    }
  };

  const addPlayer = () => {
    if (!playerInput.trim()) return;
    const name = playerInput.trim();
    const commands = {
      whitelist: `whitelist add ${name}`,
      ops: `op ${name}`,
      banned_players: `ban ${name}`,
    };
    sendCommand(commands[activeList]);
    setPlayerInput('');
  };

  const removePlayer = (name) => {
    const commands = {
      whitelist: `whitelist remove ${name}`,
      ops: `deop ${name}`,
      banned_players: `pardon ${name}`,
    };
    sendCommand(commands[activeList]);
  };

  const lists = {
    whitelist: { label: 'Whitelist', icon: Shield, color: 'text-green-400', data: data.whitelist },
    ops: { label: 'Operators', icon: Cpu, color: 'text-purple-400', data: data.ops },
    banned_players: { label: 'Banned', icon: UserX, color: 'text-red-400', data: data.banned_players },
  };

  return (
    <div className="space-y-6">
      {demo && (
        <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-2.5 text-yellow-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Demo mode — commands will be sent when Pterodactyl is connected
        </div>
      )}

      {/* List tabs */}
      <div className="flex gap-2 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
        {Object.entries(lists).map(([key, { label, icon: Icon, color }]) => (
          <button
            key={key}
            onClick={() => setActiveList(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeList === key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <Icon className={`w-3.5 h-3.5 ${activeList === key ? color : ''}`} />
            {label}
            <span className="bg-gray-700 text-gray-400 text-xs px-1.5 py-0.5 rounded-full ml-1">
              {lists[key].data?.length || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Add player */}
      <div className="flex gap-3">
        <input
          type="text"
          value={playerInput}
          onChange={(e) => setPlayerInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addPlayer()}
          placeholder={`Add player to ${lists[activeList].label.toLowerCase()}...`}
          className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm transition-colors"
        />
        <button
          onClick={addPlayer}
          className="flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {/* Player list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader className="w-6 h-6 text-green-400 animate-spin" /></div>
      ) : lists[activeList].data?.length === 0 ? (
        <div className="text-center py-12 bg-gray-900 border border-gray-800 border-dashed rounded-2xl">
          <Users className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-500">{lists[activeList].label} is empty</p>
        </div>
      ) : (
        <div className="space-y-2">
          {lists[activeList].data.map((player) => (
            <div key={player.uuid || player.name} className="flex items-center justify-between bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                <img
                  src={`https://mc-heads.net/avatar/${player.name}/32`}
                  alt={player.name}
                  className="w-8 h-8 rounded-lg"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <div className="font-semibold text-white">{player.name}</div>
                  {player.level && <div className="text-gray-500 text-xs">Op Level {player.level}</div>}
                </div>
              </div>
              <button
                onClick={() => removePlayer(player.name)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 border border-gray-700 text-gray-400 text-xs rounded-lg transition-colors"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── SETTINGS ─────────────────────────────────────────────────────
// Invite friends to co-manage the server (Pterodactyl subusers)
const AccessManager = ({ serverId }) => {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(() => {
    api.get(`/servers/${serverId}/access`).then(({ data }) => setUsers(data.users || [])).finally(() => setLoading(false));
  }, [serverId]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/servers/${serverId}/access`, { email: email.trim() });
      toast.success(data.message);
      setEmail('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}'s access to this server?`)) return;
    try {
      await api.delete(`/servers/${serverId}/access/${u.uuid}`);
      toast.success('Access removed');
      setUsers(prev => prev.filter(x => x.uuid !== u.uuid));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader className="w-6 h-6 text-green-400 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-xl">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
        <h3 className="font-bold text-white mb-1">Invite a friend</h3>
        <p className="text-gray-500 text-sm mb-4">
          They'll get an email invite with access to this server's console and files — perfect for co-owners and moderators.
        </p>
        <form onSubmit={invite} className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@example.com"
            className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-green-500 transition-colors placeholder-gray-600"
          />
          <button
            type="submit"
            disabled={inviting || !email.trim()}
            className="bg-green-500 hover:bg-green-400 disabled:opacity-40 text-gray-950 font-bold px-5 rounded-xl text-sm transition-colors"
          >
            {inviting ? 'Sending...' : 'Invite'}
          </button>
        </form>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
        <h3 className="font-bold text-white mb-4">People with access</h3>
        {users.length === 0 ? (
          <p className="text-gray-600 text-sm">Only you have access to this server.</p>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.uuid} className="flex items-center justify-between bg-gray-800/60 rounded-xl px-4 py-3">
                <div>
                  <div className="text-white text-sm">{u.email}</div>
                  <div className="text-gray-600 text-xs font-mono">{u.username}</div>
                </div>
                <button onClick={() => remove(u)} className="text-red-400 hover:text-red-300 text-xs transition-colors">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ServerSettings = ({ serverId }) => {
  const [server, setServer] = useState(null);
  const [versions, setVersions] = useState({});
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sftp, setSftp] = useState(null);
  const [sftpLoading, setSftpLoading] = useState(false);

  const generateSftp = async () => {
    if (sftp?.password && !window.confirm('Generate a new SFTP password? The old one stops working immediately.')) return;
    setSftpLoading(true);
    try {
      const { data } = await api.post(`/servers/${serverId}/sftp/password`);
      setSftp(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not generate SFTP password');
    } finally {
      setSftpLoading(false);
    }
  };

  useEffect(() => {
    api.get(`/servers/${serverId}/settings`).then(({ data }) => {
      setServer(data.server);
      setVersions(data.supported_versions || {});
      setForm({
        name: data.server.name,
        minecraft_version: data.server.minecraft_version || '1.20.4',
        server_type: data.server.server_type || 'paper',
      });
    }).finally(() => setLoading(false));
  }, [serverId]);

  // Versions available for the currently selected type
  const typeVersions = versions[form.server_type] || ['1.20.4'];

  const handleTypeChange = (newType) => {
    const list = versions[newType] || ['1.20.4'];
    setForm({
      ...form,
      server_type: newType,
      // keep version if the new type supports it, otherwise pick its newest
      minecraft_version: list.includes(form.minecraft_version) ? form.minecraft_version : list[0],
    });
  };

  const reinstall = async (skipConfirm = false, resetWorld = false) => {
    if (!skipConfirm && !window.confirm('Reinstall the server? This will reset all server files but keep your worlds. The server will be offline for a few minutes.')) return;
    try {
      await api.post(`/servers/${serverId}/settings/reinstall`, { reset_world: resetWorld });
      toast.success(resetWorld ? 'Reinstall started — the world will be reset automatically' : 'Reinstall started — this takes a few minutes');
    } catch {
      toast.error('Failed to start reinstall');
    }
  };

  const resetWorldNow = async () => {
    if (!window.confirm('Reset the world? All world data (builds, player progress) will be permanently deleted and a fresh world generated. This cannot be undone.')) return;
    try {
      const { data } = await api.post(`/servers/${serverId}/settings/reset-world`);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset world');
    }
  };

  // Compare "1.21.4"-style versions: negative if a < b
  const cmpVersions = (a, b) => {
    const pa = (a || '0').split('.').map(Number);
    const pb = (b || '0').split('.').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const d = (pa[i] || 0) - (pb[i] || 0);
      if (d !== 0) return d;
    }
    return 0;
  };

  const save = async () => {
    // Downgrading with an existing world will crash the server — warn first
    const isDowngrade =
      server?.minecraft_version &&
      form.minecraft_version &&
      cmpVersions(form.minecraft_version, server.minecraft_version) < 0;

    if (isDowngrade) {
      const ok = window.confirm(
        `You are DOWNGRADING from ${server.minecraft_version} to ${form.minecraft_version}.\n\n` +
        `Minecraft worlds cannot be loaded on older versions, so your current world must be reset.\n\n` +
        `We'll reset it automatically after the reinstall. Continue?`
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const { data } = await api.patch(`/servers/${serverId}/settings`, form);
      setServer((prev) => ({ ...prev, ...data.server }));
      if (data.requires_reinstall) {
        const msg = isDowngrade
          ? 'Settings saved! The server will be reinstalled and the world reset automatically. Reinstall now?'
          : 'Settings saved! The server needs to be reinstalled to apply the new version. Your worlds are kept. Reinstall now?';
        if (window.confirm(msg)) {
          await reinstall(true, isDowngrade);
        } else {
          toast('Reinstall later from the Danger Zone to apply the change', { icon: 'ℹ️' });
        }
      } else {
        toast.success('Settings saved!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader className="w-6 h-6 text-green-400 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-xl">
      {/* General settings */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-5">
        <h3 className="font-bold text-white">General</h3>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Server name</label>
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Server type</label>
          <select
            value={form.server_type || 'paper'}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors"
          >
            <option value="paper">Paper (recommended)</option>
            <option value="vanilla">Vanilla</option>
            <option value="forge">Forge</option>
            <option value="fabric">Fabric</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Minecraft version</label>
          <select
            value={form.minecraft_version}
            onChange={(e) => setForm({ ...form, minecraft_version: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors"
          >
            {typeVersions.map(v => (
              <option key={v} value={v}>{v}{v === typeVersions[0] ? ' (newest)' : ''}</option>
            ))}
          </select>
          <p className="text-gray-600 text-xs mt-1.5">
            The right Java version is applied automatically. Changing version or type requires a quick reinstall — your worlds are kept.
          </p>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
        >
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>

      {/* SFTP access */}
      {server?.pterodactyl_server_id && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="font-bold text-white mb-1">SFTP access</h3>
          <p className="text-gray-500 text-sm mb-4">Upload big files (modpacks, worlds) with FileZilla or WinSCP.</p>

          <div className="space-y-2 text-sm font-mono mb-4">
            <div className="flex justify-between bg-gray-800/60 rounded-lg px-3 py-2 gap-3">
              <span className="text-gray-500">Host</span>
              <button
                onClick={() => { navigator.clipboard.writeText(`sftp://${sftp?.host || 'plarixmc.com'}`); toast.success('Host copied'); }}
                className="text-white hover:text-green-400 transition-colors truncate"
              >sftp://{sftp?.host || 'plarixmc.com'}</button>
            </div>
            <div className="flex justify-between bg-gray-800/60 rounded-lg px-3 py-2">
              <span className="text-gray-500">Port</span><span className="text-white">{sftp?.port || 2022}</span>
            </div>
            {sftp?.username && (
              <div className="flex justify-between bg-gray-800/60 rounded-lg px-3 py-2 gap-3">
                <span className="text-gray-500">Username</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(sftp.username); toast.success('Username copied'); }}
                  className="text-white hover:text-green-400 transition-colors truncate"
                >{sftp.username}</button>
              </div>
            )}
          </div>

          {sftp?.password ? (
            <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4">
              <div className="text-green-400 text-sm font-semibold mb-2">Your new SFTP password</div>
              <button
                onClick={() => { navigator.clipboard.writeText(sftp.password); toast.success('Password copied'); }}
                className="w-full text-left font-mono text-white bg-gray-800 hover:bg-gray-750 rounded-lg px-3 py-2.5 text-sm break-all transition-colors"
              >
                {sftp.password} 📋
              </button>
              <p className="text-yellow-400/80 text-xs mt-2">
                Save this now — for your security it is not stored anywhere and cannot be shown again.
                You can generate a new one anytime.
              </p>
            </div>
          ) : (
            <button
              onClick={generateSftp}
              disabled={sftpLoading}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white px-4 py-2.5 rounded-xl text-sm transition-colors disabled:opacity-50"
            >
              {sftpLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Generate SFTP password
            </button>
          )}
        </div>
      )}

      {/* Server info (read only) */}
      {server && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="font-bold text-white mb-4">Server information</h3>
          <div className="space-y-3 text-sm">
            {[
              { label: 'Server ID', value: server.id },
              server.ram_mb && { label: 'RAM', value: `${server.ram_mb / 1024}GB` },
              server.disk_mb && { label: 'Storage', value: `${server.disk_mb / 1024}GB` },
              server.player_slots && { label: 'Player slots', value: server.player_slots },
              { label: 'Created', value: new Date(server.created_at).toLocaleDateString() },
              server.ip_address && { label: 'IP Address', value: `${server.ip_address}:${server.port}` },
            ].filter(Boolean).map(({ label, value }) => (
              <div key={label} className="flex justify-between py-2 border-b border-gray-800 last:border-0">
                <span className="text-gray-400">{label}</span>
                <span className="text-white font-mono text-xs">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6">
        <h3 className="font-bold text-red-400 mb-1 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Danger Zone
        </h3>
        <p className="text-gray-400 text-sm mb-4">These actions are destructive and cannot be undone.</p>
        <button
          onClick={resetWorldNow}
          className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          <Trash2 className="w-4 h-4" /> Reset world
        </button>
        <button
          onClick={() => reinstall()}
          className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Reinstall server
        </button>
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────
const ServerManagePage = () => {
  const { id } = useParams();
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('files');

  useEffect(() => {
    api.get(`/servers/${id}`).then(({ data }) => setServer(data.server)).finally(() => setLoading(false));
  }, [id]);

  const TABS = [
    { key: 'files',    label: 'Files',    icon: FolderOpen },
    { key: 'backups',  label: 'Backups',  icon: Archive },
    { key: 'players',  label: 'Players',  icon: Users },
    { key: 'upgrade',  label: 'Upgrade',  icon: Zap },
    { key: 'access',   label: 'Access',   icon: UserX },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <Loader className="w-8 h-8 text-green-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
              <Server className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white">{server?.name || 'Server'}</h1>
              <p className="text-gray-500 text-sm">{server?.plan_name} · {server?.server_type || 'Paper'} {server?.minecraft_version}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/console/${id}`}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-sm rounded-xl transition-colors"
            >
              <Terminal className="w-4 h-4" /> Console
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === key
                  ? 'bg-green-500 text-gray-950'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'files'    && <FileManager serverId={id} />}
        {tab === 'backups'  && <Backups serverId={id} />}
        {tab === 'players'  && <Players serverId={id} />}
        {tab === 'upgrade'  && <PlanUpgrade server={server} onUpgraded={(newPlan) => setServer(prev => ({ ...prev, plan_name: newPlan.name, plan_id: newPlan.id, price_monthly: newPlan.price_monthly }))} />}
        {tab === 'access'   && <AccessManager serverId={id} />}
        {tab === 'settings' && <ServerSettings serverId={id} />}
      </div>
    </div>
  );
};

export default ServerManagePage;
